import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSchoolHeadPeriod } from "@/contexts/SchoolHeadPeriodContext";

/**
 * Returns date range for the selected period.
 * If a specific term is selected → that term's range.
 * Otherwise → full academic year (min start_date → max end_date of its terms).
 */
export function usePeriodRange() {
  const { selectedYear, selectedTerm } = useSchoolHeadPeriod();
  if (selectedTerm) {
    return { start: selectedTerm.start_date, end: selectedTerm.end_date };
  }
  if (selectedYear?.terms?.length) {
    const sorted = [...selectedYear.terms].sort((a, b) =>
      a.start_date.localeCompare(b.start_date)
    );
    return {
      start: sorted[0].start_date,
      end: sorted[sorted.length - 1].end_date,
    };
  }
  return { start: null, end: null };
}

/**
 * Pull all visits in scope (school + period), paginated to bypass 1000-row limit.
 */
export function useScopedVisits() {
  const { user } = useAuth();
  const { selectedYear, selectedTerm } = useSchoolHeadPeriod();

  return useQuery({
    queryKey: [
      "sh-visits",
      user?.schoolId,
      selectedYear?.id,
      selectedTerm?.id,
    ],
    enabled: !!user?.schoolId && !!selectedYear?.id,
    queryFn: async () => {
      const all: any[] = [];
      const pageSize = 1000;
      let from = 0;
      while (true) {
        let q = supabase
          .from("visits")
          .select(
            "id,date,complaint,diagnosis,treatment,requires_followup,student_id,community_member_id,nurse_id,academic_year_id,term_id,school_id"
          )
          .eq("school_id", user!.schoolId)
          .eq("academic_year_id", selectedYear!.id)
          .order("date", { ascending: false })
          .range(from, from + pageSize - 1);
        if (selectedTerm?.id) q = q.eq("term_id", selectedTerm.id);
        const { data, error } = await q;
        if (error) throw error;
        all.push(...(data || []));
        if (!data || data.length < pageSize) break;
        from += pageSize;
      }
      return all;
    },
  });
}

export function useSchoolNurses() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["sh-nurses", user?.schoolId],
    enabled: !!user?.schoolId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id,user_id,name,display_name,is_blocked,role")
        .eq("school_id", user!.schoolId)
        .eq("role", "NURSE");
      if (error) throw error;
      return data || [];
    },
  });
}
